import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

import * as UI from "../../../../../modules/ui-kit";
import EntityCreateForm from "../../../components/EntityCreateForm";
import UpsertCategory from "../CategoryDetail/UpsertCategory.graphql";
import { canonicalTranslations } from "../categoryDefinition";

const DEFAULT_VALUES = {
    icon: "",
    name: [],
    public: false,
};

function toDefinition(values) {
    const definition: Record<string, unknown> = {
        name: canonicalTranslations(
            values.name.map(
                (translation) => (
                    {
                        language: translation.language,
                        text: translation.text,
                    }
                )
            )
        ),
        public: values.public,
    };
    if (values.icon) definition.icon = values.icon;
    return definition;
}

function renderFields({ saving, setValue, values }) {
    return (
        <>
            <UI.Field>
                <UI.FieldLabel>name</UI.FieldLabel>
                <UI.FieldDescription>It is sufficient to fill in just one (arbitrary) language when the name is the same in all languages</UI.FieldDescription>
                <UI.TranslatedInput
                    disabled={ saving }
                    label="name"
                    onChange={ (val) => setValue("name", val) }
                    value={ values.name }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>icon</UI.FieldLabel>
                <UI.FieldDescription>SVG shown next to the category in the app menu. Only main (first level) categories use an icon — subcategories leave this empty.</UI.FieldDescription>
                <UI.TextInput
                    disabled={ saving }
                    onChange={ (val) => setValue("icon", val) }
                    placeholder="https://..."
                    value={ values.icon }
                />
                { values.icon && <img alt="" className="preview" src={ values.icon } /> }
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>public</UI.FieldLabel>
                <UI.FieldDescription>Draft categories are not visible in the app until they are published</UI.FieldDescription>
                <UI.SegmentedControl
                    disabled={ saving }
                    onChange={ (val) => setValue("public", val) }
                    options={ [
                        { label: "Public", value: true },
                        { label: "Draft", value: false },
                    ] }
                    value={ values.public }
                />
            </UI.Field>
        </>
    );
}

export default function CategoryNew() {
    const [upsertCategory] = useMutation(
        gql(UpsertCategory),
        {
            awaitRefetchQueries: true,
            refetchQueries: [
                "ListCategories",
            ],
        },
    );

    async function onCreate(definition) {
        const result = await upsertCategory({ variables: { definition } });
        const payload = result.data?.upsertCategory;
        return {
            errors: payload?.errors,
            redirectTo: payload?.category?.id && `../${ payload.category.id }`,
        };
    }

    return (
        <EntityCreateForm
            createLabel="Create Category"
            defaultValues={ DEFAULT_VALUES }
            jsonDescription="Raw category JSON definition (name, icon, public, bimClasses, parentCategory)"
            onCreate={ onCreate }
            renderFields={ renderFields }
            title="New Category"
            toDefinition={ toDefinition }
        />
    );
}
